import type { GuideStep } from '../types';

/**
 * Connectors tour — each step is interactive with an animated demo that
 * moves between two ports/blocks to show what gesture to perform.
 *
 * Robustness: the demo-seed for section 'connectors' creates 2 blocks + a
 * connector between them (Index.tsx). Every step therefore has a valid
 * target on entry. `fallbackTargets` cover the edge case where the user
 * deleted the seed or entered the tour on a populated canvas.
 *
 * Animation selectors use `::eq(N)` (our custom AnimatedHighlight syntax)
 * so they resolve to the Nth matching `[data-guide="…"]` element rather
 * than CSS `:nth-of-type` which ignores the attribute filter.
 */
export const CONNECTOR_STEPS: GuideStep[] = [
  {
    id: 'connectors-1',
    page: 'index',
    section: 'connectors',
    target: 'block',
    fallbackTargets: ['layer-card', 'group-frame', 'canvas'],
    // Also light up a second block if one exists, so both anchors of the
    // "drag from A to B" gesture are visible. The multi-rect spotlight
    // renders separate rings so the direction is obvious.
    additionalHighlight: '[data-guide="block"]::eq(1)',
    title: { sv: 'Dra en koppling', en: 'Draw a connector' },
    body: {
      sv: 'Varje block har 8 portar runt kanten. Klicka på en port och dra till en port på ett annat block för att koppla ihop dem. Portarna lyser upp när du håller musen över dem.',
      en: 'Every block has 8 ports around its edges. Click a port and drag to a port on another block to connect them. Ports light up when you hover them.',
    },
    anim: {
      path: [
        '[data-guide="port-right"]::eq(0)',
        '[data-guide="port-left"]::eq(1)',
      ],
      durationMs: 3500,
      loop: true,
    },
    showMeHow: {
      // Four beats so the focus box clearly moves: first block → port on
      // block 1 → second block → port on block 2. The cursor presses at
      // port 1 and releases at port 2 so the "drag" gesture is obvious.
      steps: [
        { selector: '[data-guide="block"]::eq(0)', action: 'hover', label: 'Start block', hold: 600 },
        { selector: '[data-guide="port-right"]::eq(0)', action: 'press', label: 'Press port', hold: 500 },
        { selector: '[data-guide="block"]::eq(1)', action: 'hover', label: 'Target block', hold: 500 },
        { selector: '[data-guide="port-left"]::eq(1)', action: 'release', label: 'Release on port', hold: 800 },
      ],
      durationMs: 6000,
    },
    advanceOn: { kind: 'connectorCreated' },
    waitingHint: {
      sv: 'Dra från en port till en port på ett annat block',
      en: 'Drag from a port to another block\'s port',
    },
    placement: 'auto',
    order: 1,
  },
  {
    id: 'connectors-2',
    page: 'index',
    section: 'connectors',
    target: 'connector-path',
    fallbackTargets: ['block', 'layer-card', 'group-frame', 'canvas'],
    title: { sv: 'Waypoints', en: 'Waypoints' },
    body: {
      sv: 'Dubbelklicka på kopplingens linje för att lägga till en waypoint — en knäckpunkt som styr hur linjen dras. Dubbelklicka på en waypoint igen för att ta bort den.',
      en: 'Double-click the connector line to add a waypoint — a bend that lets you route the line. Double-click a waypoint again to remove it.',
    },
    anim: {
      path: [
        '[data-guide="connector-path"]::eq(0)',
      ],
      durationMs: 2500,
      loop: true,
    },
    showMeHow: {
      steps: [
        { selector: '[data-guide="connector-path"]::eq(0)', action: 'dblclick', label: 'Double-click the line', hold: 700 },
      ],
      durationMs: 2500,
    },
    advanceOn: { kind: 'event', name: 'connector:waypointAdded' },
    waitingHint: {
      sv: 'Dubbelklicka på kopplingens linje',
      en: 'Double-click the connector line',
    },
    placement: 'auto',
    order: 2,
  },
  {
    id: 'connectors-3',
    page: 'index',
    section: 'connectors',
    target: 'connector-midpoint',
    fallbackTargets: ['connector-path', 'block', 'layer-card', 'group-frame', 'canvas'],
    // Light up the full connector line too — tells the user where to find the
    // midpoint even if they haven't hovered yet, and guarantees the spotlight
    // covers the "+ Label" pill area even when the pill is at low opacity.
    additionalHighlight: '[data-guide="connector-path"]',
    title: { sv: 'Etikettera kopplingen', en: 'Label connectors' },
    body: {
      sv: 'Håll musen över mitten av kopplingen — en "+ Label"-knapp dyker upp. Klicka på den och skriv en etikett. Perfekt för villkorsgrenar (Ja/Nej) och namngivna kopplingar.',
      en: 'Hover the middle of the connector — a "+ Label" pill appears. Click it and type a label. Useful for conditional branches (Yes/No) and named connections.',
    },
    anim: {
      path: [
        '[data-guide="connector-path"]::eq(0)',
        '[data-guide="connector-midpoint"]::eq(0)',
      ],
      durationMs: 2500,
      loop: true,
    },
    showMeHow: {
      steps: [
        { selector: '[data-guide="connector-path"]::eq(0)', action: 'hover', label: 'Hover the line', hold: 500 },
        { selector: '[data-guide="connector-midpoint"]::eq(0)', action: 'click', label: 'Click "+ Label"', hold: 700 },
      ],
      durationMs: 3500,
    },
    advanceOn: { kind: 'event', name: 'connector:labelAdded' },
    waitingHint: {
      sv: 'Klicka på "+ Label" mitt på kopplingen',
      en: 'Click "+ Label" at the middle of the connector',
    },
    placement: 'auto',
    order: 3,
  },
];
